import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'
import type { components } from '../api/schema'

// Hooks for the work-order loop (spec 2026-08-01-01).
//
// Two audiences, two auth models, kept in one file because they are one
// workflow:
//   - the syndic side, under /api/v1, authenticated like everything else;
//   - the provider portal, under /wo/..., where the capability token in the
//     URL is the whole authorization and NO Authorization header must ever be
//     sent (the contractor has no account, and attaching a stale token from
//     localStorage would be both useless and a leak).
//
// Mirrors useInterventions.ts's hand-rolled fetch rather than the generated
// openapi-fetch client, for consistency with the rest of the domain.

// ---- Types ----

export interface WorkOrderPortalDocument {
    name: string
    file_name?: string
    uploaded_at: string
}

export interface WorkOrderEquipment {
    uid: string
    name: string
    type?: string
    origin: 'selected' | 'declared'
    pending: boolean
}

export interface WorkOrderPortalView {
    organization_name: string
    reference: string
    title: string
    description?: string
    kind: string
    status: string
    asset_name?: string
    asset_address?: string
    contract_reference?: string
    order_ref?: string
    provider_name?: string
    os_sent_at?: string | null
    os_responded_at?: string | null
    os_response?: 'accepted' | 'declined' | null
    scheduled_at?: string | null
    performed_at?: string | null
    work_submitted_at?: string | null
    validation_status?: 'approved' | 'disputed' | null
    dispute_reason?: string
    decline_reason?: string
    report_note?: string
    documents: WorkOrderPortalDocument[]
    equipment: WorkOrderEquipment[]
    expires_at?: string | null
}

export interface EquipmentChoice {
    uid: string
    name: string
    type_code: string
    type_name: string
    location?: string
    declared: boolean
}

export interface EquipmentTypeChoice {
    uid: string
    code: string
    name: string
}

export interface EquipmentOptions {
    existing: EquipmentChoice[]
    types: EquipmentTypeChoice[]
    trade_filtered: boolean
}

export interface PendingEquipment {
    uid: string
    name: string
    type_name?: string
    similar: EquipmentChoice[]
}

export interface InterventionValidation {
    work_submitted_at?: string | null
    validation_status?: 'approved' | 'disputed' | null
    validated_at?: string | null
    dispute_reason?: string
    report_note?: string
    pending_equipment: PendingEquipment[]
    touched_equipment: WorkOrderEquipment[]
    /** The intervention's charge lines and their recoverable proposals (spec 2026-10-25-09). */
    charge_lines?: ValidationChargeLine[]
}

export type ValidationChargeLine = components['schemas']['InterventionValidation']['charge_lines'] extends (infer T)[] | undefined ? T : never
export type RecoverableDecision = components['schemas']['RecoverableDecision']

export interface EquipmentDecision {
    asset_uid: string
    action: 'confirm' | 'merge' | 'reject'
    merge_into_uid?: string
}

export interface ValidateInput {
    verdict: 'approved' | 'disputed'
    reason?: string
    equipment?: EquipmentDecision[]
    /** Recoverable decisions on the charge lines, with an approval only. */
    recoverable?: RecoverableDecision[]
}

// ---- Provider portal (public, token-authorized) ----

const portalBase = (orgCode: string, token: string) =>
    `${apiBaseUrl}/wo/${encodeURIComponent(orgCode)}/${encodeURIComponent(token)}`

/**
 * portalFetch deliberately sends no Authorization header. The token in the path
 * is the entire authorization; the caller is an external contractor with no
 * account. Every unresolvable request comes back as a bare 404 with a
 * non-JSON body, which is why the error path does not try to parse one.
 */
async function portalFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, options)
    if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw { status: res.status, ...body }
    }
    return res.json()
}

export const workOrderKey = (orgCode: string, token: string) =>
    ['work-order-portal', orgCode, token] as const

export function useWorkOrderPortal(orgCode: string, token: string) {
    return useQuery({
        queryKey: workOrderKey(orgCode, token),
        queryFn: () => portalFetch<WorkOrderPortalView>(`${portalBase(orgCode, token)}/state`),
        enabled: !!orgCode && !!token,
        retry: false,
    })
}

export function useWorkOrderEquipmentOptions(orgCode: string, token: string, enabled: boolean) {
    return useQuery({
        queryKey: ['work-order-equipment', orgCode, token],
        queryFn: () => portalFetch<EquipmentOptions>(`${portalBase(orgCode, token)}/equipment`),
        enabled: enabled && !!orgCode && !!token,
        retry: false,
    })
}

/** usePortalAction wires one POST endpoint of the portal to the shared cache. */
function usePortalAction<TInput>(orgCode: string, token: string, path: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (input: TInput) =>
            portalFetch<WorkOrderPortalView>(`${portalBase(orgCode, token)}/${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input ?? {}),
            }),
        onSuccess: (view) => qc.setQueryData(workOrderKey(orgCode, token), view),
    })
}

export const useAcceptWorkOrder = (orgCode: string, token: string) =>
    usePortalAction<{ scheduled_at: string; note?: string }>(orgCode, token, 'accept')

export const useDeclineWorkOrder = (orgCode: string, token: string) =>
    usePortalAction<{ reason: string }>(orgCode, token, 'decline')

export const useSubmitWorkOrder = (orgCode: string, token: string) =>
    usePortalAction<{ performed_at?: string; note?: string; equipment_uids?: string[] }>(
        orgCode,
        token,
        'submit',
    )

export function useUploadWorkOrderDocument(orgCode: string, token: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (file: File) => {
            const form = new FormData()
            form.append('file', file)
            // No Content-Type header: the browser must set the multipart
            // boundary itself.
            return portalFetch<WorkOrderPortalView>(`${portalBase(orgCode, token)}/documents`, {
                method: 'POST',
                body: form,
            })
        },
        onSuccess: (view) => qc.setQueryData(workOrderKey(orgCode, token), view),
    })
}

export function useDeclareEquipment(orgCode: string, token: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (input: { asset_type_uid: string; name: string; note?: string; plate?: File | null }) => {
            const form = new FormData()
            form.append('asset_type_uid', input.asset_type_uid)
            form.append('name', input.name)
            if (input.note) form.append('note', input.note)
            if (input.plate) form.append('plate', input.plate)
            return portalFetch<{ uid: string; name: string; type: string; pending: boolean }>(
                `${portalBase(orgCode, token)}/equipment`,
                { method: 'POST', body: form },
            )
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: workOrderKey(orgCode, token) })
            qc.invalidateQueries({ queryKey: ['work-order-equipment', orgCode, token] })
        },
    })
}

// ---- Syndic side (authenticated) ----

function authFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const jwt = localStorage.getItem('token')
    return fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
            ...options?.headers,
        },
    }).then(async (res) => {
        if (!res.ok) {
            const body = await res.json().catch(() => ({}))
            throw { status: res.status, ...body }
        }
        return res.json()
    })
}

const validationKey = (org: string, uid: string) => ['intervention-validation', org, uid] as const

export function useInterventionValidation(org: string, interventionUid: string, enabled = true) {
    return useQuery({
        queryKey: validationKey(org, interventionUid),
        queryFn: () =>
            authFetch<InterventionValidation>(
                `/api/v1/organizations/${org}/interventions/${interventionUid}/validation`,
            ),
        enabled: enabled && !!org && !!interventionUid,
    })
}

export function useValidateWork(org: string, interventionUid: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (input: ValidateInput) =>
            authFetch<InterventionValidation>(
                `/api/v1/organizations/${org}/interventions/${interventionUid}/validation`,
                { method: 'POST', body: JSON.stringify(input) },
            ),
        onSuccess: (view) => {
            qc.setQueryData(validationKey(org, interventionUid), view)
            qc.invalidateQueries({ queryKey: ['intervention-detail', org, interventionUid] })
            qc.invalidateQueries({ queryKey: ['intervention-events', org, interventionUid] })
        },
    })
}

export interface SendWorkOrderInput {
    recipients: string[]
    subject?: string
    body?: string
}

export interface SendWorkOrderResult {
    portal_url: string
    /** The `email` job carrying the OS, when one was queued. */
    job_uid?: string
    /** Whether an e-mail was actually queued — never assume it was. */
    email_queued: boolean
}

/**
 * useSendWorkOrder mints (rotating) the provider link, stamps os_sent_at and —
 * when a reviewed subject and body are passed — queues the ordre de service
 * e-mail.
 *
 * The subject and body used to be collected by the dialog and dropped on the
 * floor: the endpoint only minted a link, so the OS was never sent while the UI
 * reported success (spec 2026-08-03-01). They are now part of the request, and
 * the response says plainly whether anything was queued.
 */
export function useSendWorkOrder(org: string, interventionUid: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (input: SendWorkOrderInput) =>
            authFetch<SendWorkOrderResult>(
                `/api/v1/organizations/${org}/interventions/${interventionUid}/work-order`,
                { method: 'POST', body: JSON.stringify(input) },
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['intervention-detail', org, interventionUid] })
            qc.invalidateQueries({ queryKey: ['intervention-events', org, interventionUid] })
        },
    })
}
